import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StarIcon from "@mui/icons-material/Star";

import Container from "../../components/Container";
import { getRestaurantById, getRestaurantContent } from "./contentManagerData";

const COLORS = {
  primary: "#2DD736",
  dark: "#053418",
  cta: "#0A5529",
  grey: "#375D46",
  greenGray: "#E5EAD9",
  lighter: "#D5F4D7",
  white: "#FFFFFF",
};

const STAGE_MAP = {
  PUBLISHED: { bg: "#D5F4D7", color: "#0A5529", label: "Published" },
  READY: { bg: "#E0F2FE", color: "#0369A1", label: "Ready" },
  SCHEDULED: { bg: "#FEF3C7", color: "#92400E", label: "Scheduled" },
  DRAFT: { bg: "#F3F4F6", color: "#6B7280", label: "Draft" },
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StageChip = ({ stage }) => {
  const s = STAGE_MAP[stage] || STAGE_MAP.DRAFT;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{ fontFamily: "GilroySemiBold", fontSize: 10.5, backgroundColor: s.bg, color: s.color, height: 20 }}
    />
  );
};

const TypeBadge = ({ type }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      px: 0.75,
      py: 0.25,
      borderRadius: "6px",
      backgroundColor: "rgba(5,52,24,0.72)",
      color: COLORS.white,
    }}
  >
    {type === "video" ? <MovieOutlinedIcon sx={{ fontSize: 13 }} /> : <ImageOutlinedIcon sx={{ fontSize: 13 }} />}
    <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: 10, textTransform: "capitalize" }}>{type}</Typography>
  </Box>
);

const ContentManagerHistory = (props) => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();

  const restaurant = useMemo(() => getRestaurantById(restaurantId), [restaurantId]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setItems(getRestaurantContent(restaurantId));
  }, [restaurantId]);

  const filtered = useMemo(() => {
    let list = items.filter((it) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        (it.generation_prompt || "").toLowerCase().includes(q) ||
        (it.caption || "").toLowerCase().includes(q);
      const matchType = typeFilter === "all" || it.type === typeFilter;
      const matchStage = stageFilter === "all" || it.content_stage === stageFilter;
      return matchSearch && matchType && matchStage;
    });
    list = [...list].sort((a, b) =>
      sortOrder === "newest"
        ? (b.created_at || "").localeCompare(a.created_at || "")
        : (a.created_at || "").localeCompare(b.created_at || "")
    );
    return list;
  }, [items, search, typeFilter, stageFilter, sortOrder]);

  // Mock soft-delete — mirrors DELETE /api/v1/chat/{generation_record_id}/history.
  const handleDelete = (item) => {
    setItems((prev) => prev.filter((x) => x.generation_record_id !== item.generation_record_id));
    setSelected(null);
  };

  if (!restaurant) {
    return (
      <Container {...props} page="content-manager" onSearch={() => {}}>
        <Box sx={{ maxWidth: 600 }}>
          <Typography sx={{ fontFamily: "GilroyBold", fontSize: 20, color: COLORS.dark, mb: 1 }}>
            Restaurant not found
          </Typography>
          <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 14, color: COLORS.grey, mb: 2 }}>
            We couldn't find content for this restaurant.
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/content-manager")}
            sx={{ fontFamily: "GilroySemiBold", textTransform: "none", color: COLORS.cta }}
          >
            Back to Content Manager
          </Button>
        </Box>
      </Container>
    );
  }

  const imageCount = items.filter((it) => it.type === "image").length;
  const videoCount = items.filter((it) => it.type === "video").length;

  return (
    <Container {...props} page="content-manager" onSearch={() => {}}>
      <Box sx={{ maxWidth: 1400 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <IconButton
            onClick={() => navigate("/content-manager")}
            size="small"
            sx={{ color: COLORS.cta, backgroundColor: COLORS.white, border: "1px solid #E5EAD9" }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Avatar src={restaurant.logo_url} alt={restaurant.restaurant_name} sx={{ width: 44, height: 44 }} />
          <Box>
            <Typography sx={{ fontFamily: "GilroyBold", fontSize: 22, color: COLORS.dark, lineHeight: 1.2 }}>
              {restaurant.restaurant_name}
            </Typography>
            <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 13, color: COLORS.grey }}>
              {restaurant.location} · {items.length} generated item{items.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", backgroundColor: COLORS.white, border: "1px solid #E5EAD9" }}>
              <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 12.5, color: COLORS.grey }}>Total</Typography>
              <Typography sx={{ fontFamily: "GilroyBold", fontSize: 24, color: COLORS.cta }}>{items.length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", backgroundColor: COLORS.white, border: "1px solid #E5EAD9" }}>
              <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 12.5, color: COLORS.grey }}>Images</Typography>
              <Typography sx={{ fontFamily: "GilroyBold", fontSize: 24, color: COLORS.dark }}>{imageCount}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", backgroundColor: COLORS.white, border: "1px solid #E5EAD9" }}>
              <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 12.5, color: COLORS.grey }}>Videos / Reels</Typography>
              <Typography sx={{ fontFamily: "GilroyBold", fontSize: 24, color: COLORS.dark }}>{videoCount}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: "12px", backgroundColor: COLORS.white, border: "1px solid #E5EAD9", mb: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search prompt or caption..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                  sx: { fontFamily: "GilroyRegular", fontSize: 13, borderRadius: "8px" },
                }}
                sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}
              />
            </Grid>
            <Grid item xs={6} sm={2.5}>
              <Select fullWidth size="small" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                sx={{ fontFamily: "GilroySemiBold", fontSize: 13, borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}>
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="image">Images</MenuItem>
                <MenuItem value="video">Videos</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6} sm={2.5}>
              <Select fullWidth size="small" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                sx={{ fontFamily: "GilroySemiBold", fontSize: 13, borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}>
                <MenuItem value="all">All Stages</MenuItem>
                <MenuItem value="PUBLISHED">Published</MenuItem>
                <MenuItem value="READY">Ready</MenuItem>
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Select fullWidth size="small" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}
                sx={{ fontFamily: "GilroySemiBold", fontSize: 13, borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5EAD9" } }}>
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Paper>

        {/* Gallery */}
        {filtered.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, borderRadius: "12px", backgroundColor: COLORS.white, border: "1px solid #E5EAD9", textAlign: "center" }}>
            <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 14, color: COLORS.grey }}>
              No content matches your filters.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {filtered.map((item) => (
              <Grid item xs={6} sm={4} md={3} key={item.generation_record_id}>
                <Paper
                  elevation={0}
                  onClick={() => setSelected(item)}
                  sx={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: COLORS.white,
                    border: "1px solid #E5EAD9",
                    cursor: "pointer",
                    transition: "box-shadow 0.15s ease, transform 0.15s ease",
                    "&:hover": { boxShadow: "0 6px 18px rgba(5,52,24,0.12)", transform: "translateY(-2px)" },
                  }}
                >
                  <Box sx={{ position: "relative", aspectRatio: "1 / 1", backgroundColor: "#F3F4F6" }}>
                    <img
                      src={item.thumbnail_url || item.content_url}
                      alt={item.caption || "content"}
                      loading="lazy"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    <Box sx={{ position: "absolute", top: 8, left: 8 }}>
                      <TypeBadge type={item.type} />
                    </Box>
                    {item.is_favourite && (
                      <StarIcon sx={{ position: "absolute", top: 8, right: 8, fontSize: 18, color: "#FBBF24" }} />
                    )}
                    {item.type === "video" && (
                      <PlayCircleOutlineIcon
                        sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: 44, color: "rgba(255,255,255,0.92)" }}
                      />
                    )}
                  </Box>
                  <Box sx={{ p: 1.25 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                      <StageChip stage={item.content_stage} />
                      <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 10.5, color: "#9CA3AF" }}>
                        {fmtDateTime(item.created_at).split(",")[0]}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: "GilroyRegular",
                        fontSize: 12,
                        color: "#4B5563",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: 32,
                      }}
                    >
                      {item.generation_prompt}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Detail dialog */}
      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
      >
        {selected && (
          <DialogContent sx={{ p: 0 }}>
            <Grid container>
              <Grid item xs={12} md={6} sx={{ backgroundColor: "#0B130D", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
                {selected.type === "video" ? (
                  <video src={selected.content_url} poster={selected.thumbnail_url} controls style={{ width: "100%", maxHeight: 480 }} />
                ) : (
                  <img src={selected.content_url || selected.thumbnail_url} alt={selected.caption || "content"} style={{ width: "100%", maxHeight: 480, objectFit: "contain" }} />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TypeBadge type={selected.type} />
                      <StageChip stage={selected.content_stage} />
                    </Box>
                    <IconButton size="small" onClick={() => setSelected(null)}>
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>

                  {selected.caption && (
                    <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: 14, color: COLORS.dark, mb: 1.5 }}>
                      {selected.caption}
                    </Typography>
                  )}

                  <DetailRow label="Prompt" value={selected.generation_prompt} />
                  <DetailRow label="Type" value={selected.type} />
                  <DetailRow label="Content type" value={selected.content_type} />
                  <DetailRow label="Stage" value={selected.content_stage} />
                  <DetailRow label="Status" value={selected.status} />
                  <DetailRow label="AI model" value={selected.ai_model} />
                  <DetailRow label="Aspect ratio" value={selected.aspect_ratio} />
                  <DetailRow label="Created" value={fmtDateTime(selected.created_at)} />
                  <DetailRow label="Content ID" value={selected.content_id} mono />
                  <DetailRow label="Generation ID" value={selected.generation_record_id} mono />

                  <Box sx={{ mt: 2.5, display: "flex", gap: 1 }}>
                    <Button
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => handleDelete(selected)}
                      sx={{ fontFamily: "GilroySemiBold", textTransform: "none", color: "#B91C1C", borderColor: "#FCA5A5" }}
                      variant="outlined"
                      size="small"
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
        )}
      </Dialog>
    </Container>
  );
};

const DetailRow = ({ label, value, mono }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 1, py: 0.5, borderBottom: "1px solid #F3F4F6" }}>
    <Typography sx={{ fontFamily: "GilroyRegular", fontSize: 12, color: "#9CA3AF" }}>{label}</Typography>
    <Typography
      sx={{
        fontFamily: mono ? "monospace" : "GilroyRegular",
        fontSize: mono ? 11 : 12.5,
        color: "#374151",
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

export default ContentManagerHistory;
